import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { useAuth } from '../contexts/AuthContext';
import { Brain, Plus, Search, MoreHorizontal, Pencil, Trash2, MessageSquare, Database, UserCircle, Clock, Volume2, Upload, Loader2, Mic, Crown, Share2, Eye, Users, Bell, Sparkles, TrendingUp, FileText } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { Checkbox } from '../components/ui/checkbox';
import { VoiceUpsellDialog } from '../components/voice/VoiceUpsellDialog';
import { BehaviourDialog } from '../components/behaviour/BehaviourDialog';
import { KnowledgeDialog } from '../components/knowledge/KnowledgeDialog';
import { AnalyticsDialog } from '../components/analytics/AnalyticsDialog';
import { hasVoiceStandard, hasVoiceClone } from '../lib/voice-access';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClonesPage() {
  const { token, user, tenant } = useAuth();
  const navigate = useNavigate();
  const [clones, setClones] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedClone, setSelectedClone] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    whatsapp_number: '',
    job_title: '',
    department: '',
    linked_user_id: ''
  });

  // Voice settings state
  const [isVoiceOpen, setIsVoiceOpen] = useState(false);
  const [voiceClone, setVoiceClone] = useState(null);
  const [voiceSettings, setVoiceSettings] = useState(null);
  const [voiceLoading, setVoiceLoading] = useState(false);
  const [voiceSaving, setVoiceSaving] = useState(false);
  const [elVoices, setElVoices] = useState([]);
  const [elVoicesLoading, setElVoicesLoading] = useState(false);
  const [cloneVoiceLoading, setCloneVoiceLoading] = useState(false);
  const [cloneVoiceName, setCloneVoiceName] = useState('');
  const [cloneVoiceFile, setCloneVoiceFile] = useState(null);
  const [testPlaying, setTestPlaying] = useState(false);

  // Voice upsell dialog
  const [upsellOpen, setUpsellOpen] = useState(false);
  const [upsellFeature, setUpsellFeature] = useState(null);
  const audioTestRef = useRef(null);

  // Sharing state
  const [sharingOpen, setSharingOpen] = useState(false);
  const [sharingClone, setSharingClone] = useState(null);
  const [sharingMode, setSharingMode] = useState('private');
  const [sharedWith, setSharedWith] = useState([]);
  const [sharingSaving, setSharingSaving] = useState(false);

  // Access info (read-only for linked users)
  const [accessOpen, setAccessOpen] = useState(false);
  const [accessInfo, setAccessInfo] = useState(null);
  const [accessLoading, setAccessLoading] = useState(false);

  // Notifications
  const [notifications, setNotifications] = useState([]);

  // Behaviour dialog
  const [behaviourOpen, setBehaviourOpen] = useState(false);
  const [behaviourClone, setBehaviourClone] = useState(null);

  // Knowledge dialog
  const [knowledgeOpen, setKnowledgeOpen] = useState(false);
  const [knowledgeClone, setKnowledgeClone] = useState(null);

  // Analytics dialog
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [analyticsClone, setAnalyticsClone] = useState(null);

  const canManage = user?.role === 'owner' || user?.role === 'admin';

  useEffect(() => {
    fetchClones();
    if (canManage) fetchUsers();
    fetchNotifications();
  }, []);

  const fetchClones = async () => {
    try {
      const response = await axios.get(`${API}/persons`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClones(response.data);
    } catch (error) {
      toast.error('Failed to load clones');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      // Users endpoint requires admin, silently skip for user role
    }
  };

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/persons/notifications/unread`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data);
    } catch {}
  };

  const markNotifRead = async (notifId) => {
    try {
      await axios.patch(`${API}/persons/notifications/${notifId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.filter(n => n.id !== notifId));
    } catch {}
  };

  const openSharingModal = (clone) => {
    setSharingClone(clone);
    setSharingMode(clone.sharing_mode || 'private');
    setSharedWith(clone.shared_with || []);
    setSharingOpen(true);
    // Fetch users if not already loaded (for non-admin who got promoted)
    if (users.length === 0) fetchUsers();
  };

  const handleSaveSharing = async () => {
    if (!sharingClone) return;
    setSharingSaving(true);
    try {
      await axios.patch(`${API}/persons/${sharingClone.id}/sharing`, {
        sharing_mode: sharingMode,
        shared_with: sharingMode === 'shared' ? sharedWith : [],
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Partage mis à jour');
      setSharingOpen(false);
      fetchClones();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur mise à jour partage');
    } finally {
      setSharingSaving(false);
    }
  };

  const toggleUserSharing = (userId) => {
    setSharedWith(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const openAccessInfo = async (clone) => {
    setAccessOpen(true);
    setAccessLoading(true);
    try {
      const res = await axios.get(`${API}/persons/${clone.id}/access`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccessInfo(res.data);
    } catch (error) {
      toast.error('Impossible de charger les informations d\'accès');
    } finally {
      setAccessLoading(false);
    }
  };

  // Check if a clone has a "new" notification for current user
  const hasNewNotif = (cloneId) => notifications.some(n => n.person_id === cloneId);
  const getNotifForClone = (cloneId) => notifications.find(n => n.person_id === cloneId);
  // Check if clone is shared with current user (not own clone)
  const isSharedWithMe = (clone) => clone.linked_user_id !== user?.id && (clone.shared_with || []).includes(user?.id);

  const handleCreate = async () => {
    try {
      const payload = { ...formData };
      if (!payload.linked_user_id) delete payload.linked_user_id;
      await axios.post(`${API}/persons`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Clone created successfully');
      setIsCreateOpen(false);
      setFormData({ name: '', email: '', phone: '', whatsapp_number: '', job_title: '', department: '', linked_user_id: '' });
      fetchClones();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create clone');
    }
  };

  const handleUpdate = async () => {
    try {
      const updateData = { ...formData };
      delete updateData.email; // email can't be changed via patch in this form easily
      if (updateData.linked_user_id === '') updateData.linked_user_id = null;
      await axios.patch(`${API}/persons/${selectedClone.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Clone updated successfully');
      setIsEditOpen(false);
      setSelectedClone(null);
      fetchClones();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update clone');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this clone?')) return;
    try {
      await axios.delete(`${API}/persons/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Clone deleted successfully');
      fetchClones();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete clone');
    }
  };

  const openEdit = (clone) => {
    setSelectedClone(clone);
    setFormData({
      name: clone.name,
      email: clone.email || '',
      phone: clone.phone || '',
      whatsapp_number: clone.whatsapp_number || '',
      job_title: clone.job_title || '',
      department: clone.department || '',
      linked_user_id: clone.linked_user_id || ''
    });
    setIsEditOpen(true);
  };

  const openVoiceSettings = async (clone) => {
    setVoiceClone(clone);
    setIsVoiceOpen(true);
    setVoiceLoading(true);
    setElVoices([]);
    setCloneVoiceName('');
    setCloneVoiceFile(null);
    try {
      const res = await axios.get(`${API}/persons/${clone.id}/voice/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVoiceSettings(res.data);
      if (res.data.elevenlabs_available) {
        fetchElVoices(clone.id);
      }
    } catch (error) {
      toast.error('Erreur chargement paramètres voix');
    } finally {
      setVoiceLoading(false);
    }
  };

  const fetchElVoices = async (personId) => {
    setElVoicesLoading(true);
    try {
      const res = await axios.get(`${API}/persons/${personId}/voice/elevenlabs/voices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setElVoices(res.data.voices || []);
    } catch (error) {
      console.error('Failed to load ElevenLabs voices');
    } finally {
      setElVoicesLoading(false);
    }
  };

  const handleSaveVoice = async () => {
    if (!voiceClone || !voiceSettings) return;
    setVoiceSaving(true);
    try {
      await axios.patch(`${API}/persons/${voiceClone.id}/voice/settings`, {
        voice_provider: voiceSettings.provider,
        openai_voice: voiceSettings.openai_voice,
        elevenlabs_voice_id: voiceSettings.elevenlabs_voice_id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Paramètres voix sauvegardés');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur sauvegarde');
    } finally {
      setVoiceSaving(false);
    }
  };

  const handleCloneVoice = async () => {
    if (!voiceClone || !cloneVoiceFile || !cloneVoiceName.trim()) return;
    setCloneVoiceLoading(true);
    try {
      const formData = new FormData();
      formData.append('files', cloneVoiceFile);
      formData.append('voice_name', cloneVoiceName);
      const res = await axios.post(`${API}/persons/${voiceClone.id}/voice/clone`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Voix "${res.data.name}" clonée avec succès`);
      setCloneVoiceName('');
      setCloneVoiceFile(null);
      // Refresh voice settings and voices list
      const settingsRes = await axios.get(`${API}/persons/${voiceClone.id}/voice/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVoiceSettings(settingsRes.data);
      fetchElVoices(voiceClone.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur clonage vocal');
    } finally {
      setCloneVoiceLoading(false);
    }
  };

  const handleTestVoice = async () => {
    if (!voiceClone || !voiceSettings) return;
    if (testPlaying && audioTestRef.current) {
      audioTestRef.current.pause();
      audioTestRef.current = null;
      setTestPlaying(false);
      return;
    }
    setTestPlaying(true);
    try {
      // Save current settings first so the test uses the selected voice
      await axios.patch(`${API}/persons/${voiceClone.id}/voice/settings`, {
        voice_provider: voiceSettings.provider,
        openai_voice: voiceSettings.openai_voice,
        elevenlabs_voice_id: voiceSettings.elevenlabs_voice_id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const res = await axios.post(`${API}/persons/${voiceClone.id}/speak`, {
        text: `Bonjour, je suis le clone digital de ${voiceClone.name}. Comment puis-je vous aider ?`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const audioBytes = Uint8Array.from(atob(res.data.audio), c => c.charCodeAt(0));
      const blob = new Blob([audioBytes], { type: 'audio/mpeg' });
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioTestRef.current = audio;
      audio.onended = () => { setTestPlaying(false); URL.revokeObjectURL(url); };
      audio.play();
    } catch (error) {
      toast.error('Erreur test vocal');
      setTestPlaying(false);
    }
  };

  const filteredClones = clones.filter(clone =>
    clone.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    clone.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    clone.department?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <DashboardLayout title="Clones">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-muted-foreground">Manage your digital clones and their configurations</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search clones..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-64"
                data-testid="search-clones"
              />
            </div>
            {canManage && (
              <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
                <DialogTrigger asChild>
                  <Button className="gap-2" data-testid="create-clone-btn">
                    <Plus className="w-4 h-4" />
                    New Clone
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New Clone</DialogTitle>
                    <DialogDescription>Add a new digital clone for your team member</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Name *</Label>
                      <Input
                        id="name"
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        data-testid="clone-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="john@company.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        data-testid="clone-email-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="job_title">Job Title</Label>
                        <Input
                          id="job_title"
                          placeholder="Sales Manager"
                          value={formData.job_title}
                          onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                          data-testid="clone-job-title-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="department">Department</Label>
                        <Input
                          id="department"
                          placeholder="Sales"
                          value={formData.department}
                          onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                          data-testid="clone-department-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        placeholder="+33 6 12 34 56 78"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        data-testid="clone-phone-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="whatsapp">WhatsApp</Label>
                      <Input
                        id="whatsapp"
                        placeholder="+33698765432"
                        value={formData.whatsapp_number}
                        onChange={(e) => setFormData({ ...formData, whatsapp_number: e.target.value })}
                        data-testid="clone-whatsapp-input"
                      />
                    </div>
                    {canManage && users.length > 0 && (
                      <div className="space-y-2">
                        <Label htmlFor="linked_user">Linked User</Label>
                        <Select
                          value={formData.linked_user_id}
                          onValueChange={(value) => setFormData({ ...formData, linked_user_id: value === '_none' ? '' : value })}
                        >
                          <SelectTrigger data-testid="clone-linked-user-select">
                            <SelectValue placeholder="No user linked" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none">No user linked</SelectItem>
                            {users.map((u) => (
                              <SelectItem key={u.id} value={u.id}>
                                {u.first_name} {u.last_name} ({u.email})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">Link this clone to a team member account</p>
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
                    <Button onClick={handleCreate} disabled={!formData.name} data-testid="save-clone-btn">
                      Create Clone
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Clones Table */}
        <Card data-testid="clones-table-card">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Clone</TableHead>
                  <TableHead>Department</TableHead>
                  {canManage && <TableHead>Linked User</TableHead>}
                  <TableHead>Memories</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : filteredClones.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={canManage ? 6 : 5} className="text-center py-8 text-muted-foreground">
                      {searchQuery ? 'No clones found matching your search' : 'No clones yet. Create your first clone to get started.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredClones.map((clone) => {
                    const shared = isSharedWithMe(clone);
                    const notif = getNotifForClone(clone.id);
                    return (
                    <TableRow key={clone.id} className="group" data-testid={`clone-row-${clone.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-9 w-9">
                            <AvatarImage src={clone.avatar_url} />
                            <AvatarFallback className="bg-primary/10 text-primary">
                              {clone.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{clone.name}</p>
                              {shared && (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 border-blue-400/50 text-blue-400 gap-1" data-testid={`shared-badge-${clone.id}`}>
                                  <Share2 className="w-3 h-3" />
                                  Partagé
                                </Badge>
                              )}
                              {clone.sharing_mode === 'shared' && canManage && (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 border-emerald-400/50 text-emerald-400 gap-1">
                                  <Users className="w-3 h-3" />
                                  {(clone.shared_with || []).length}
                                </Badge>
                              )}
                              {notif && (
                                <Badge className="text-[10px] px-1.5 py-0 h-5 bg-amber-500 text-white animate-pulse cursor-pointer" data-testid={`new-badge-${clone.id}`}
                                  onClick={() => markNotifRead(notif.id)}>
                                  Nouveau
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {shared ? `Clone de ${clone.name} • ${clone.job_title || 'No title'}` : (clone.job_title || 'No title')}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {clone.department || '-'}
                      </TableCell>
                      {canManage && (
                        <TableCell className="text-muted-foreground text-sm">
                          {(() => {
                            const lu = users.find(u => u.id === clone.linked_user_id);
                            return lu ? (
                              <span className="flex items-center gap-1.5">
                                <UserCircle className="w-3.5 h-3.5" />
                                {lu.first_name} {lu.last_name}
                              </span>
                            ) : <span className="text-muted-foreground/50">-</span>;
                          })()}
                        </TableCell>
                      )}
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Brain className="w-4 h-4 text-muted-foreground" />
                          <span>{clone.memory_count}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={clone.is_active ? 'default' : 'secondary'}>
                          {clone.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/clones/${clone.id}/chat`)}
                            className="gap-1.5"
                            data-testid={`chat-clone-${clone.id}`}
                          >
                            <MessageSquare className="w-4 h-4" />
                            Chat
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/clones/${clone.id}/chat?report=weekly`)}
                            className="gap-1.5"
                            data-testid={`report-clone-${clone.id}`}
                          >
                            <FileText className="w-4 h-4" />
                            Rapport
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (hasVoiceStandard(user, tenant)) {
                                navigate(`/clones/${clone.id}/voice`);
                              } else {
                                setUpsellFeature('voice_standard');
                                setUpsellOpen(true);
                              }
                            }}
                            className="gap-1"
                            data-testid={`voice-chat-clone-${clone.id}`}
                          >
                            <Mic className="w-3.5 h-3.5" />
                            Parler
                            <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 border-blue-300 text-blue-600 ml-0.5">Standard</Badge>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (hasVoiceClone(user, tenant)) {
                                openVoiceSettings(clone);
                              } else {
                                setUpsellFeature('voice_clone');
                                setUpsellOpen(true);
                              }
                            }}
                            className="gap-1"
                            data-testid={`voice-clone-${clone.id}`}
                          >
                            <Volume2 className="w-3.5 h-3.5" />
                            Voix clonee
                            <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 border-violet-300 text-violet-600 ml-0.5">Premium</Badge>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/clones/${clone.id}/timeline`)}
                            className="gap-1.5"
                            data-testid={`timeline-clone-${clone.id}`}
                          >
                            <Clock className="w-4 h-4" />
                            Timeline
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/clones/${clone.id}/sources`)}
                            className="gap-1.5"
                            data-testid={`sources-clone-${clone.id}`}
                          >
                            <Database className="w-4 h-4" />
                            Sources
                          </Button>
                          {canManage && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`clone-actions-${clone.id}`}>
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openSharingModal(clone)} data-testid={`share-clone-${clone.id}`}>
                                  <Share2 className="w-4 h-4 mr-2" />
                                  Gérer les accès
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => { setBehaviourClone(clone); setBehaviourOpen(true); }} data-testid={`behaviour-clone-${clone.id}`}>
                                  <Sparkles className="w-4 h-4 mr-2" />
                                  Comportement
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => { setKnowledgeClone(clone); setKnowledgeOpen(true); }} data-testid={`knowledge-clone-${clone.id}`}>
                                  <Brain className="w-4 h-4 mr-2" />
                                  Knowledge
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => { setAnalyticsClone(clone); setAnalyticsOpen(true); }} data-testid={`analytics-clone-${clone.id}`}>
                                  <TrendingUp className="w-4 h-4 mr-2" />
                                  Suivi
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openEdit(clone)} data-testid={`edit-clone-${clone.id}`}>
                                  <Pencil className="w-4 h-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDelete(clone.id)} className="text-destructive" data-testid={`delete-clone-${clone.id}`}>
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                          {/* Linked user can view who has access + behaviour */}
                          {!canManage && clone.linked_user_id === user?.id && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => { setBehaviourClone(clone); setBehaviourOpen(true); }} className="gap-1 text-xs" data-testid={`behaviour-clone-${clone.id}`}>
                                <Sparkles className="w-3.5 h-3.5" />
                                Comportement
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => { setKnowledgeClone(clone); setKnowledgeOpen(true); }} className="gap-1 text-xs" data-testid={`knowledge-clone-${clone.id}`}>
                                <Brain className="w-3.5 h-3.5" />
                                Knowledge
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => openAccessInfo(clone)} className="gap-1 text-xs" data-testid={`view-access-${clone.id}`}>
                                <Eye className="w-3.5 h-3.5" />
                                Qui a accès
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Clone</DialogTitle>
              <DialogDescription>Update clone information</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Name *</Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="edit-clone-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="edit-clone-email"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-job_title">Job Title</Label>
                  <Input
                    id="edit-job_title"
                    value={formData.job_title}
                    onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                    data-testid="edit-clone-job-title"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-department">Department</Label>
                  <Input
                    id="edit-department"
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    data-testid="edit-clone-department"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-phone">Phone</Label>
                <Input
                  id="edit-phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="edit-clone-phone"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-whatsapp">WhatsApp</Label>
                <Input
                  id="edit-whatsapp"
                  placeholder="+33698765432"
                  value={formData.whatsapp_number}
                  onChange={(e) => setFormData({ ...formData, whatsapp_number: e.target.value })}
                  data-testid="edit-clone-whatsapp"
                />
              </div>
              {canManage && users.length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="edit-linked_user">Linked User</Label>
                  <Select
                    value={formData.linked_user_id || '_none'}
                    onValueChange={(value) => setFormData({ ...formData, linked_user_id: value === '_none' ? '' : value })}
                  >
                    <SelectTrigger data-testid="edit-clone-linked-user">
                      <SelectValue placeholder="No user linked" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">No user linked</SelectItem>
                      {users.map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.first_name} {u.last_name} ({u.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditOpen(false)}>Cancel</Button>
              <Button onClick={handleUpdate} disabled={!formData.name} data-testid="update-clone-btn">
                Update Clone
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Voice Settings Dialog */}
        <Dialog open={isVoiceOpen} onOpenChange={setIsVoiceOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Volume2 className="w-5 h-5" />
                Voix de {voiceClone?.name}
              </DialogTitle>
              <DialogDescription>Configurez la voix de synthèse pour ce clone</DialogDescription>
            </DialogHeader>

            {voiceLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : voiceSettings ? (
              <div className="space-y-6 py-2">
                {/* Provider Selection */}
                <div className="space-y-2">
                  <Label>Type de voix</Label>
                  <Select
                    value={voiceSettings.provider}
                    onValueChange={(v) => setVoiceSettings({ ...voiceSettings, provider: v })}
                    data-testid="voice-provider-select"
                  >
                    <SelectTrigger data-testid="voice-provider-trigger">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="openai">Standard (OpenAI)</SelectItem>
                      <SelectItem value="elevenlabs" disabled={!voiceSettings.elevenlabs_available}>
                        Premium (ElevenLabs) {!voiceSettings.elevenlabs_available && '— Clé requise'}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {!voiceSettings.elevenlabs_available && (
                    <p className="text-xs text-muted-foreground">
                      Pour utiliser les voix premium, ajoutez votre clé ElevenLabs dans{' '}
                      <button onClick={() => { setIsVoiceOpen(false); window.location.href = '/settings'; }} className="underline text-primary">
                        Paramètres
                      </button>
                    </p>
                  )}
                </div>

                <Separator />

                {/* OpenAI Voice Selection */}
                {voiceSettings.provider === 'openai' && (
                  <div className="space-y-2">
                    <Label>Voix OpenAI</Label>
                    <Select
                      value={voiceSettings.openai_voice}
                      onValueChange={(v) => setVoiceSettings({ ...voiceSettings, openai_voice: v })}
                    >
                      <SelectTrigger data-testid="openai-voice-trigger">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(voiceSettings.openai_voices || []).map((v) => (
                          <SelectItem key={v} value={v}>
                            {v.charAt(0).toUpperCase() + v.slice(1)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      9 voix disponibles. "Nova" et "Shimmer" sont recommandées pour le français.
                    </p>
                  </div>
                )}

                {/* ElevenLabs Voice Selection */}
                {voiceSettings.provider === 'elevenlabs' && voiceSettings.elevenlabs_available && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Voix ElevenLabs</Label>
                      {elVoicesLoading ? (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                          <Loader2 className="w-4 h-4 animate-spin" /> Chargement des voix...
                        </div>
                      ) : (
                        <Select
                          value={voiceSettings.elevenlabs_voice_id || '_default'}
                          onValueChange={(v) => setVoiceSettings({ ...voiceSettings, elevenlabs_voice_id: v === '_default' ? '' : v })}
                        >
                          <SelectTrigger data-testid="elevenlabs-voice-trigger">
                            <SelectValue placeholder="Sélectionner une voix" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_default">Voix par défaut (Rachel)</SelectItem>
                            {elVoices.map((v) => (
                              <SelectItem key={v.voice_id} value={v.voice_id}>
                                {v.name} <span className="text-xs text-muted-foreground ml-1">({v.category})</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>

                    <Separator />

                    {/* Voice Cloning Section */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Mic className="w-4 h-4 text-primary" />
                        <Label className="text-sm font-semibold">Cloner une voix</Label>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Uploadez un fichier audio (MP3, WAV) de la personne pour créer une voix clonée.
                      </p>
                      <div className="space-y-2">
                        <Input
                          placeholder="Nom de la voix (ex: Justine FR)"
                          value={cloneVoiceName}
                          onChange={(e) => setCloneVoiceName(e.target.value)}
                          data-testid="clone-voice-name-input"
                        />
                        <div className="flex gap-2">
                          <label
                            className="flex-1 flex items-center gap-2 px-3 py-2 border border-input rounded-md cursor-pointer hover:bg-accent transition-colors text-sm"
                            data-testid="clone-voice-file-label"
                          >
                            <Upload className="w-4 h-4 text-muted-foreground" />
                            <span className="text-muted-foreground truncate">
                              {cloneVoiceFile ? cloneVoiceFile.name : 'Choisir un fichier audio...'}
                            </span>
                            <input
                              type="file"
                              accept="audio/*"
                              className="hidden"
                              onChange={(e) => setCloneVoiceFile(e.target.files?.[0] || null)}
                              data-testid="clone-voice-file-input"
                            />
                          </label>
                          <Button
                            size="sm"
                            onClick={handleCloneVoice}
                            disabled={cloneVoiceLoading || !cloneVoiceName.trim() || !cloneVoiceFile}
                            data-testid="clone-voice-submit-btn"
                          >
                            {cloneVoiceLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Cloner'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <Separator />

                {/* Test Voice */}
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleTestVoice}
                    disabled={testPlaying}
                    className="gap-2"
                    data-testid="test-voice-btn"
                  >
                    {testPlaying ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Lecture...</>
                    ) : (
                      <><Volume2 className="w-4 h-4" /> Tester la voix</>
                    )}
                  </Button>
                  <span className="text-xs text-muted-foreground">Écouter un exemple avec les paramètres actuels</span>
                </div>
              </div>
            ) : null}

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsVoiceOpen(false)}>Fermer</Button>
              <Button onClick={handleSaveVoice} disabled={voiceSaving || voiceLoading} data-testid="save-voice-btn">
                {voiceSaving ? 'Sauvegarde...' : 'Sauvegarder'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Sharing Modal (Owner) */}
      <Dialog open={sharingOpen} onOpenChange={setSharingOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5" />
              Gérer les accès — {sharingClone?.name}
            </DialogTitle>
            <DialogDescription>Contrôlez qui peut accéder à ce clone</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Mode de partage</Label>
              <Select value={sharingMode} onValueChange={setSharingMode}>
                <SelectTrigger data-testid="sharing-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="private">Privé — Owner + utilisateur lié uniquement</SelectItem>
                  <SelectItem value="shared">Partagé — Accès sélectif</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {sharingMode === 'shared' && (
              <div className="space-y-3">
                <Label>Utilisateurs autorisés</Label>
                <div className="border rounded-md max-h-48 overflow-y-auto" data-testid="sharing-users-list">
                  {users.filter(u => u.id !== sharingClone?.linked_user_id).map(u => (
                    <label key={u.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-muted/50 cursor-pointer border-b last:border-b-0">
                      <Checkbox
                        checked={sharedWith.includes(u.id)}
                        onCheckedChange={() => toggleUserSharing(u.id)}
                        data-testid={`share-user-${u.id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{u.first_name} {u.last_name}</p>
                        <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                      </div>
                      {u.id === sharingClone?.linked_user_id && (
                        <Badge variant="secondary" className="text-[10px] shrink-0">Lié</Badge>
                      )}
                    </label>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  {sharedWith.length} utilisateur{sharedWith.length > 1 ? 's' : ''} sélectionné{sharedWith.length > 1 ? 's' : ''}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSharingOpen(false)}>Annuler</Button>
            <Button onClick={handleSaveSharing} disabled={sharingSaving} data-testid="save-sharing-btn">
              {sharingSaving ? 'Sauvegarde...' : 'Sauvegarder'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Access Info Modal (Read-only for linked user) */}
      <Dialog open={accessOpen} onOpenChange={setAccessOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Qui a accès à mon clone
            </DialogTitle>
          </DialogHeader>
          {accessLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : accessInfo ? (
            <div className="space-y-3 py-2">
              <div className="flex items-center gap-2">
                <Badge variant={accessInfo.sharing_mode === 'shared' ? 'default' : 'secondary'}>
                  {accessInfo.sharing_mode === 'shared' ? 'Partagé' : 'Privé'}
                </Badge>
              </div>
              {accessInfo.shared_with_users.length > 0 ? (
                <div className="space-y-2" data-testid="access-users-list">
                  <p className="text-sm text-muted-foreground">Utilisateurs avec accès :</p>
                  {accessInfo.shared_with_users.map(u => (
                    <div key={u.id} className="flex items-center gap-2 px-3 py-2 bg-muted/30 rounded text-sm">
                      <UserCircle className="w-4 h-4 text-muted-foreground" />
                      <span>{u.first_name} {u.last_name}</span>
                      <span className="text-xs text-muted-foreground">({u.email})</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Personne d'autre n'a accès à votre clone.</p>
              )}
              <p className="text-xs text-muted-foreground italic">La gestion des accès est réservée à l'administrateur.</p>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAccessOpen(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <VoiceUpsellDialog
        open={upsellOpen}
        onOpenChange={setUpsellOpen}
        requiredFeature={upsellFeature}
      />

      <BehaviourDialog
        open={behaviourOpen}
        onOpenChange={setBehaviourOpen}
        clone={behaviourClone}
      />

      <KnowledgeDialog
        open={knowledgeOpen}
        onOpenChange={setKnowledgeOpen}
        clone={knowledgeClone}
      />

      <AnalyticsDialog
        open={analyticsOpen}
        onOpenChange={setAnalyticsOpen}
        clone={analyticsClone}
      />
    </DashboardLayout>
  );
}
